import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { AddressDTO, BalanceDetailsDTO, BankAccountDTO, BankCardDTO, BankCardDTOCardTypeEnum, BankCardDTOProcessAsCardTypeEnum, ResidentSearchResponseV2Dto, ResidentV2Dto, ScheduledAutoPayDto } from '@inosio/payments-sdk';
import { BankCardTypeDto, CreateBankAccountDTO, CreateBankCardDTO, FeesDTO, PaidByEnum, PaymentMethodsDTO, PaymentsConfigDto, TransactionDTO } from '@inosio/payments-sdk';

interface ResidentDataPage {
  Rows: Array<ResidentV2Dto>;
}

export const DEFAULT_ADHOC_CARD_UUID = 'adhoc-card-uuid-0000';
export const DEFAULT_ADHOC_BANK_UUID = 'adhoc-bank-uuid-0000';

type ResidentsState = {
  residentDataPages: Array<ResidentDataPage>;
  residentsHistory: ResidentSearchResponseV2Dto;
  isFetchingResidents: Boolean;
  isGetResidentsFailed: Boolean;
  isSuccess: Boolean;
  errorMessage: ErrorType;
  isSendingInvitationSuccess: Boolean;
  isSendingInprogress: Boolean;
  isIssuingTempPasscodeSuccess: Boolean;
  isIssuingTempPasscodeFailed: Boolean;
  issuingTempPasscodeError: string;
  sendingInvitationError: string;
  isSendingInvitationFailed: Boolean;
  isEditingResident: Boolean;
  isEditingSuccess: Boolean;
  residentBalanceDetails: BalanceDetailsDTO;
  residentPaymentConfigs: PaymentsConfigDto;
  paymentFeesByResident: FeesDTO[];
  currentPaymentMethods: PaymentMethodsDTO;
  adhocBankCard: CreateBankCardDTO;
  adhocBankAccount: CreateBankAccountDTO;
  isFetchingAdhocOTPResponse: boolean;
  isAdhocOTPSuccess: boolean;
  isAdhocOTPError: boolean;
  adhocOTPResponse: TransactionDTO;
  adhocOTPError: string | null;
  isFetchingResidentBalanceDetails: boolean;
  isResidentBalanceDetailsSuccess: boolean;
  isResidentBalanceDetailsError: boolean;
  residentBalanceDetailsError: string | null;
  isFetchingResidentPaymentFees: boolean;
  isResidentPaymentFeesSuccess: boolean;
  isResidentPaymentFeesError: boolean;
  residentPaymentFeesError: string | null;
  isBankCardTypeError: boolean;
  getBankTypeError: string | null;
  getBankTypeResponse: BankCardTypeDto;
  residentTransactionHistory: TransactionDTO[];
  residentAutoPayHistory: ScheduledAutoPayDto[];
  newlyAddedBankCard: BankCardDTO;
  newlyAddedBankAccount: BankAccountDTO;
  IsSilentReload: boolean;
};

const getInitialState = () => {
  return {
    residentDataPages: [],
    residentsHistory: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    isFetchingResidents: true,
    isGetResidentsFailed: false,
    isSuccess: false,
    errorMessage: {} as ErrorType,
    isSendingInvitationSuccess: false,
    isSendingInprogress: true,
    isIssuingTempPasscodeSuccess: false,
    isIssuingTempPasscodeFailed: false,
    issuingTempPasscodeError: '',
    sendingInvitationError: '',
    isSendingInvitationFailed: false,
    isEditingResident: false,
    isEditingSuccess: false,
    // placeholder
    residentBalanceDetails: {
      Balance: 0,
      MinPayment: 0,
      MaxPayment: 0,
      PropertyPaymentTypes: [
        { PropertyPaymentTypeID: 0, Label: 'amount owed', Amount: 0 },
        { PropertyPaymentTypeID: 0, Label: 'security deposit', Amount: 0 },
        { PropertyPaymentTypeID: 0, Label: 'misc', Amount: 0 },
      ],
      ChargeDetails: [{ ChargeDescription: 'testing', ChargeAmount: 0 }],
      PaymentMethodAllowed: false,
      LeaseLastUpdatedAt: new Date().toString(),
    },
    residentPaymentConfigs: {
      AutoPayConfigs: [],
      PaymentTypeConfigs: [],
      PaymentRestrictions: [],
      SinglePaymentMaxLimit: 750000,
    },
    paymentFeesByResident: [],
    currentPaymentMethods: { BankAccounts: [], BankCards: [] },
    adhocBankCard: {} as CreateBankCardDTO,
    adhocBankAccount: {} as CreateBankAccountDTO,
    isFetchingAdhocOTPResponse: false,
    isAdhocOTPSuccess: false,
    isAdhocOTPError: false,
    adhocOTPResponse: {} as TransactionDTO,
    adhocOTPError: null,
    isFetchingResidentBalanceDetails: false,
    isResidentBalanceDetailsSuccess: false,
    isResidentBalanceDetailsError: false,
    residentBalanceDetailsError: null,
    isFetchingResidentPaymentFees: false,
    isResidentPaymentFeesSuccess: false,
    isResidentPaymentFeesError: false,
    residentPaymentFeesError: null,
    isBankCardTypeError: false,
    getBankTypeError: null,
    getBankTypeResponse: {} as BankCardTypeDto,
    residentTransactionHistory: [],
    residentAutoPayHistory: [],
    newlyAddedBankCard: {} as BankCardDTO,
    newlyAddedBankAccount: {} as BankAccountDTO,
    IsSilentReload: false,
  } as ResidentsState;
};

const resetPaymentStates = (state: any) => {
  return {
    ...state,
    residentBalanceDetails: {
      Balance: 0,
      MinPayment: 0,
      MaxPayment: 0,
      PropertyPaymentTypes: [
        { PropertyPaymentTypeID: 0, Label: 'amount owed', Amount: 0 },
        { PropertyPaymentTypeID: 0, Label: 'security deposit', Amount: 0 },
        { PropertyPaymentTypeID: 0, Label: 'misc', Amount: 0 },
      ],
      ChargeDetails: [{ ChargeDescription: 'testing', ChargeAmount: 0 }],
    },
    residentPaymentConfigs: {
      AutoPayConfigs: [],
      PaymentTypeConfigs: [],
    },
    paymentFeesByResident: [],
    currentPaymentMethods: { BankAccounts: [], BankCards: [] },
    adhocBankCard: {} as CreateBankCardDTO,
    adhocBankAccount: {} as CreateBankAccountDTO,
    isFetchingAdhocOTPResponse: false,
    isAdhocOTPSuccess: false,
    isAdhocOTPError: false,
    adhocOTPResponse: {} as TransactionDTO,
    adhocOTPError: null,
    isFetchingResidentBalanceDetails: false,
    isResidentBalanceDetailsSuccess: false,
    isResidentBalanceDetailsError: false,
    residentBalanceDetailsError: null,
    isFetchingResidentPaymentFees: false,
    isResidentPaymentFeesSuccess: false,
    isResidentPaymentFeesError: false,
    residentPaymentFeesError: null,
    isBankCardTypeError: false,
    getBankTypeError: null,
    getBankTypeResponse: {} as BankCardTypeDto,
    residentTransactionHistory: [],
    residentAutoPayHistory: [],
  };
};

const pmResidentsSlice = createSlice({
  name: 'pmResidents',
  initialState: getInitialState(),
  reducers: {
    getResidentsData(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isFetchingResidents = true;
      state.IsSilentReload = !!action.payload.IsSilentReload;
    },
    setResidentsData(state, action) {
      /*
     this function has one responsiblity: receive updates from API
     and put the results in state.
     */
      let _payload = action.payload as any;

      //get a copy of the current book
      let currentResidentsPages = [...state.residentDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.RecordStart === 0) currentResidentsPages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil(_payload.RecordStart / _payload.RangeSize);

      //now, add this new data page to our book
      currentResidentsPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };

      //return the full
      let residentsHistoryDetailsData = {
        residentsHistory: action.payload,
        residentDataPages: currentResidentsPages,
        isFetchingResidents: false,
        isSuccess: true,
      };

      return { ...state, ...residentsHistoryDetailsData };
    },
    getResidentsDataFailed(state, action) {
      state.isGetResidentsFailed = true;
      state.isFetchingResidents = false;
      state.errorMessage = action.payload;
      state.isSuccess = false;
    },
    getEditResidentData(state, action) {
      state.isEditingResident = true;
      state.isEditingSuccess = false;
    },
    setEditResidentData(state, action) {
      state.isEditingResident = false;
      state.isEditingSuccess = true;
    },
    setEditResidentError(state, action) {
      state.errorMessage = action.payload;
    },
    setIssueTempPasscodeResult(state, action) {
      const tempPasscodeResponse = {
        tempPasscodeDetails: action.payload,
        isIssuingTempPasscodeSuccess: true,
        isIssuingTempPasscodeFailed: false,
      };
      return { ...state, ...tempPasscodeResponse };
    },
    getCreateAdhocOTP(state, action) {
      state.isFetchingAdhocOTPResponse = true;
      state.isAdhocOTPError = false;
      state.isAdhocOTPSuccess = false;
    },
    setCreateAdhocOTPResponse(state, action) {
      return {
        ...state,
        isFetchingAdhocOTPResponse: false,
        isAdhocOTPSuccess: true,
        isAdhocOTPError: false,
        adhocOTPResponse: { ...action.payload },
        adhocOTPError: null,
      };
    },
    setCreateAdhocOTPError(state, action) {
      return {
        ...state,
        isFetchingAdhocOTPResponse: false,
        isAdhocOTPSuccess: false,
        isAdhocOTPError: true,
        adhocOTPError: action.payload,
      };
    },
    getResidentBankCardType(state, action) {},
    setResidentBankCardTypeResponse(state, action) {
      return { ...state, getBankTypeResponse: { ...action.payload }, isBankCardTypeError: false, getBankTypeError: null };
    },
    setResidentBankCardTypeError(state, action) {
      return { ...state, isBankCardTypeError: false, getBankTypeError: { ...action.payload } };
    },
    setCreateBankCardRequest(state, action) {
      // build bankCardDto from card creation dto for use in adhoc otp
      const bankCardDto = {
        AccountLastFour: action.payload.CardNumber.slice(-4),
        CardType: action.payload.CardType ?? (state.getBankTypeResponse.CardType && state.getBankTypeResponse.CardType === 'CC' ? BankCardDTOCardTypeEnum.Cc : BankCardDTOCardTypeEnum.Db),
        ProcessAsCardType:
          action.payload.ProcessAsCardType ??
          (state.getBankTypeResponse.ProcessAsCardType && state.getBankTypeResponse.ProcessAsCardType === 'CC' ? BankCardDTOProcessAsCardTypeEnum.Cc : BankCardDTOProcessAsCardTypeEnum.Db),
        Uuid: DEFAULT_ADHOC_CARD_UUID, // just a dummy so we don't break resident OTP, we'd discard it for pm adhoc otp anyway
        CreatedDate: new Date().toISOString(),
        ExpirationDate: action.payload.ExpirationDate,
        CardNetwork: action.payload.CardNetwork ?? state.getBankTypeResponse.CardNetwork,
        CardholderFirstname: action.payload.CardholderFirstname,
        CardholderLastname: action.payload.CardholderLastname,
        NickName: action.payload.NickName,
        BillingAddress: action.payload.BillingAddress as AddressDTO,
        IsNotAdhoc: action.payload.IsNotAdhoc,
      } as BankCardDTO;

      return {
        ...state,
        newlyAddedBankCard: { ...bankCardDto },
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankCards: [{ ...bankCardDto }],
        },
        adhocBankCard: action.payload,
      };
    },
    setUpdateBankCardRequest(state, action) {
      return {
        ...state,
        newlyAddedBankCard: { ...state.newlyAddedBankCard, ...action.payload },
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankCards: [{ ...state.currentPaymentMethods.BankCards[0], ...action.payload }],
        },
        adhocBankCard: { ...state.adhocBankCard, ...action.payload },
      };
    },
    setDeleteBankCardRequest(state, action) {
      return {
        ...state,
        newlyAddedBankCard: {} as BankCardDTO,
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankCards: [],
        },
        adhocBankCard: {} as CreateBankCardDTO,
      };
    },
    setCreateBankAccountRequest(state, action) {
      // build bankAccountDto from account creation dto for use in pm adhoc otp
      const bankAccountDto = {
        AccountType: action.payload.AccountType,
        Uuid: DEFAULT_ADHOC_BANK_UUID,
        AccountLastFour: action.payload.AccountNumber.slice(-4),
        CreatedDate: new Date().toISOString(),
        RoutingNumber: action.payload.RoutingNumber,
        AccountHolderFirstname: action.payload.AccountHolderFirstname,
        AccountHolderLastname: action.payload.AccountHolderLastname,
        NickName: action.payload.NickName,
        BankName: action.payload.BankName,
        BillingAddress: action.payload.BillingAddress as AddressDTO,
        IsNotAdhoc: action.payload.IsNotAdhoc,
      } as BankAccountDTO;

      return {
        ...state,
        newlyAddedBankAccount: { ...bankAccountDto },
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankAccounts: [{ ...bankAccountDto }],
        },
        adhocBankAccount: action.payload,
      };
    },
    setUpdateBankAccountRequest(state, action) {
      return {
        ...state,
        newlyAddedBankAccount: { ...state.newlyAddedBankAccount, ...action.payload },
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankAccounts: [{ ...state.currentPaymentMethods.BankAccounts[0], ...action.payload }],
        },
        adhocBankAccount: { ...state.adhocBankAccount, ...action.payload },
      };
    },
    setDeleteBankAccountRequest(state, action) {
      return {
        ...state,
        newlyAddedBankAccount: {} as BankAccountDTO,
        currentPaymentMethods: {
          ...state.currentPaymentMethods,
          BankAccounts: [],
        },
        adhocBankAccount: {} as CreateBankAccountDTO,
      };
    },
    getResidentBalanceDetails(state, action) {
      state.isFetchingResidentBalanceDetails = true;
      state.isResidentBalanceDetailsSuccess = false;
      state.isResidentBalanceDetailsError = false;
    },
    setResidentBalanceDetailsResponse(state, action) {
      return {
        ...state,
        isFetchingResidentBalanceDetails: false,
        isResidentBalanceDetailsSuccess: true,
        isResidentBalanceDetailsError: false,
        residentBalanceDetails: { ...action.payload },
        residentBalanceDetailsError: null,
      };
    },
    setResidentBalanceDetailsError(state, action) {
      return {
        ...state,
        isFetchingResidentBalanceDetails: false,
        isResidentBalanceDetailsSuccess: false,
        isResidentBalanceDetailsError: true,
        residentBalanceDetailsError: { ...action.payload },
      };
    },
    getResidentPaymentFees(state, action) {
      state.isFetchingResidentPaymentFees = true;
      state.isResidentPaymentFeesSuccess = false;
      state.isResidentPaymentFeesError = false;
    },
    setResidentPaymentFeesResponse(state, action) {
      const rawFees = action.payload as FeesDTO[];
      const feesRedacted = rawFees.map((rf) => {
        return rf.PaidBy === PaidByEnum.Payer ? rf : { ...rf, FeeAmount: 0 };
      }); //if res, keep as is, otherwise if PM make 0
      return {
        ...state,
        isFetchingResidentPaymentFees: false,
        isResidentPaymentFeesSuccess: true,
        isResidentPaymentFeesError: false,
        residentPaymentFeesError: null,
        paymentFeesByResident: feesRedacted,
      };
    },
    setResidentPaymentFeesError(state, action) {
      return {
        ...state,
        isFetchingResidentPaymentFees: false,
        isResidentPaymentFeesSuccess: false,
        isResidentPaymentFeesError: true,
        residentPaymentFeesError: { ...action.payload },
      };
    },
    clearPaymentStates(state, action) {
      return { ...resetPaymentStates(state) };
    },
  },
});
export const {
  setResidentPaymentFeesError,
  setResidentPaymentFeesResponse,
  getResidentPaymentFees,
  setResidentBalanceDetailsError,
  setResidentBalanceDetailsResponse,
  getResidentBalanceDetails,
  setCreateBankAccountRequest,
  setCreateBankCardRequest,
  setCreateAdhocOTPError,
  setCreateAdhocOTPResponse,
  getCreateAdhocOTP,
  getResidentsData,
  setResidentsData,
  getResidentsDataFailed,
  setIssueTempPasscodeResult,
  getEditResidentData,
  setEditResidentData,
  setEditResidentError,
  getResidentBankCardType,
  setResidentBankCardTypeResponse,
  setResidentBankCardTypeError,
  clearPaymentStates,
  setUpdateBankCardRequest,
  setUpdateBankAccountRequest,
  setDeleteBankAccountRequest,
  setDeleteBankCardRequest,
} = pmResidentsSlice.actions;

export default pmResidentsSlice.reducer;

export const selectResidentsHistory = (state: RootState) => state.pmResidentsModuleState.residentsHistory;
export const selectResidentsCurrentPages = (state: RootState) => state.pmResidentsModuleState.residentDataPages;
export const selectResidentPaymentConfigs = (state: RootState) => state.pmResidentsModuleState.residentPaymentConfigs;
export const selectResidentBalanceDetails = (state: RootState) => state.pmResidentsModuleState.residentBalanceDetails;
export const selectResidentResidentFees = (state: RootState) => state.pmResidentsModuleState.paymentFeesByResident;
export const selectAvailablePaymentMethods = (state: RootState) => state.pmResidentsModuleState.currentPaymentMethods;
export const selectResidentBankCardType = (state: RootState) => state.pmResidentsModuleState.getBankTypeResponse;
export const selectAdhocBankAccount = (state: RootState) => state.pmResidentsModuleState.adhocBankAccount;
export const selectAdhocBankCard = (state: RootState) => state.pmResidentsModuleState.adhocBankCard;
export const selectAdhocOtpDetails = (state: RootState) => state.pmResidentsModuleState.adhocOTPResponse;
export const selectResidentTransactionHistory = (state: RootState) => state.pmResidentsModuleState.residentTransactionHistory;
export const selectResidentAutoPayHistory = (state: RootState) => state.pmResidentsModuleState.residentAutoPayHistory;
export const selectResidents = (state: RootState) => state.pmResidentsModuleState;
export const selectResidentNewBankAccount = (state: RootState) => state.pmResidentsModuleState.newlyAddedBankAccount;
export const selectResidentNewBankCard = (state: RootState) => state.pmResidentsModuleState.newlyAddedBankCard;
